import { createElement, FC, useMemo, useState } from 'react';
import { Avatar, Button, Col, Divider, Popover, Row, Segmented, Statistic } from 'antd';
import { capitalCase } from 'change-case';
import { TbCircleArrowUp, TbInfoCircle, TbRosetteDiscount } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { BillingInterval, FEATURE_ADD_ON_CONFIG, FeatureAddOnKey } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { getEnabledBillingEntitlementKeys } from '@/shared/entitlements';
import {
    getAnnualTotal,
    getDerivedDiscount,
    getFeatureAddOnCapabilities,
    getFeatureAddOnFeatures,
    getMonthlyEquivalent,
} from '@/shared/pricing';
import { useUserStore } from '@/stores';

import { NormalizedPricingItem } from '..';
import { BillingEntitlementList } from '../../billing';
import { OurDocsLink, SmallTag } from '../../tags';

interface FeatureAddOnPopoverProps {
    defaultInterval?: BillingInterval;
    itemKey: FeatureAddOnKey;
    unitLabel?: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export function normalizeAddOn(addOnKey: FeatureAddOnKey): NormalizedPricingItem {
    const addon = FEATURE_ADD_ON_CONFIG[addOnKey];

    return {
        key: addon.key,
        label: addon.label,
        description: addon.description,
        pricing: addon.pricing,
        supportedIntervals: addon.supportedIntervals ?? ['monthly', 'annual'],
        features: addon.features ?? [],
        capabilities: addon.capabilities ?? {},
    };
}

const FeatureAddOnPopover: FC<FeatureAddOnPopoverProps> = ({
    defaultInterval = 'monthly',
    itemKey,
    unitLabel = '/active seat/month',
}): JSX.Element => {
    const item = normalizeAddOn(itemKey);

    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    const { featureAddOnKeyIconToMap } = useMaps();

    const [interval, setInterval] = useState<BillingInterval>(defaultInterval);

    const hasPricing = Boolean(item.pricing);
    const intervalSupported = item.supportedIntervals.includes(interval);

    const monthlyPrice = useMemo(
        () => (hasPricing && intervalSupported ? getMonthlyEquivalent(item.pricing, interval) : null),
        [item, interval, hasPricing, intervalSupported],
    );

    const annualTotal = useMemo(() => getAnnualTotal(item.pricing), [item.pricing]);

    const discount = useMemo(() => getDerivedDiscount(item.pricing), [item.pricing]);

    const intervalOptions = item.supportedIntervals.map((i) => ({
        value: i,
        label: capitalCase(i),
    }));

    const capabilities = useMemo(() => getFeatureAddOnCapabilities(itemKey), [itemKey]);
    const features = useMemo(() => getFeatureAddOnFeatures(itemKey), [itemKey]);
    const capabilityEntitlements = useMemo(
        () => getEnabledBillingEntitlementKeys(capabilities).map((entitlementKey) => ({ entitlementKey })),
        [capabilities],
    );

    const { selectedUser } = useUserStore();

    const popoverContent = (
        <div style={{ width: 16 * 24 }}>
            {/* HEADER */}
            <div
                style={{
                    padding: '0.5rem 0.75rem',
                    borderBottom: `1px solid ${token.colorBorder}`,
                }}
            >
                <Row align='middle'>
                    <Col flex='none'>
                        <small style={{ color: token.colorTextTertiary }}>Add-On Details</small>
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <Segmented
                            size='small'
                            options={intervalOptions}
                            value={interval}
                            onChange={(v) => setInterval(v as BillingInterval)}
                        />
                    </Col>
                </Row>
            </div>

            {/* PRICE */}
            <section
                style={{
                    padding: '0.75rem',
                    borderBottom: `1px solid ${token.colorBorder}`,
                }}
            >
                {hasPricing && monthlyPrice !== null ? (
                    <>
                        <Row align='middle' gutter={[4, 0]}>
                            <Col flex='none'>
                                <span style={{ color: token.colorTextTertiary }}>$</span>
                            </Col>
                            <Col flex='none'>
                                <Statistic value={monthlyPrice} style={{ margin: 0, padding: 0 }} />
                            </Col>
                            <Col flex='auto'>
                                <span
                                    style={{
                                        color: token.colorTextTertiary,
                                        verticalAlign: '1px',
                                    }}
                                >
                                    {unitLabel}
                                </span>
                            </Col>
                        </Row>

                        {interval === 'annual' && annualTotal && (
                            <Row align={'middle'}>
                                <Col flex={'none'}>
                                    <small style={{ color: token.colorTextTertiary, verticalAlign: '1px' }}>
                                        Billed annually at $
                                        <span style={{ color: token.colorTextSecondary }}>
                                            {Number(annualTotal)?.toLocaleString()}
                                        </span>
                                        /active seat
                                    </small>
                                </Col>
                                <Col flex={'none'}>
                                    {discount > 0 && (
                                        <Row align={'middle'}>
                                            <Col>
                                                <SmallTag
                                                    message={
                                                        <>
                                                            <TbRosetteDiscount
                                                                style={{
                                                                    color: token.green7,
                                                                    verticalAlign: '-0.075rem',
                                                                    marginRight: '0.125rem',
                                                                }}
                                                            />
                                                            {discount}% Off
                                                        </>
                                                    }
                                                    styles={{
                                                        backgroundColor: token.green2,
                                                        color: token.green7,
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            </Row>
                        )}
                    </>
                ) : (
                    <small style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                        <OurDocsLink type='docs' text={'Contact Sales'} href={'https://countercyclical.com/contact'} />{' '}
                        or{' '}
                        <OurDocsLink
                            type='calendar'
                            text={'Book an Enterprise Demo'}
                            href={'https://cal.com/countercyclical/enterprise-demo'}
                        />
                    </small>
                )}
            </section>

            {/* FEATURES */}
            <section style={{ padding: '0.75rem' }}>
                <small
                    style={{
                        color: token.colorTextTertiary,
                        display: 'block',
                        marginBottom: 6,
                    }}
                >
                    Description
                </small>
                <span>{item.description}</span>
            </section>
            <Divider style={{ margin: 0 }} />
            <section style={{ padding: '0.75rem' }}>
                <small
                    style={{
                        color: token.colorTextTertiary,
                        display: 'block',
                        marginBottom: 6,
                    }}
                >
                    Core Features:
                </small>

                <BillingEntitlementList
                    items={features.map((feature) => ({
                        entitlementKey: feature.key,
                        included: feature.included,
                        label: feature.label,
                    }))}
                />
            </section>

            {capabilityEntitlements.length > 0 && (
                <>
                    <Divider style={{ margin: 0 }} />
                    <section style={{ padding: '0.75rem' }}>
                        <small
                            style={{
                                color: token.colorTextTertiary,
                                display: 'block',
                                marginBottom: 6,
                            }}
                        >
                            Additional Capabilities:
                        </small>
                        <BillingEntitlementList items={capabilityEntitlements} />
                    </section>
                </>
            )}

            {selectedUser?.onboardingCompleted && (
                <>
                    <Divider style={{ marginBottom: '0.5rem', marginTop: 0 }} />
                    <div style={{ padding: '0 0.75rem' }}>
                        <Button block type='primary' onClick={() => navigate('/settings/account/plans/addons')}>
                            <TbCircleArrowUp size='1rem' />
                            Upgrade
                        </Button>
                    </div>
                </>
            )}

            <Divider style={{ marginBottom: '0.5rem', marginTop: selectedUser?.onboardingCompleted ? '0.5rem' : 0 }} />

            {/* FOOTER */}
            <div style={{ padding: '0 0.75rem 0.5rem' }}>
                <small style={{ color: token.colorTextTertiary }}>
                    <TbInfoCircle
                        style={{
                            verticalAlign: '-0.1rem',
                            marginRight: '0.25rem',
                        }}
                    />
                    Learn more on <OurDocsLink href='https://countercyclical.com/pricing' text='our website' /> or in{' '}
                    <OurDocsLink />
                </small>
            </div>
        </div>
    );

    const IconComponent = featureAddOnKeyIconToMap[item?.key as FeatureAddOnKey];

    return (
        <Popover
            content={popoverContent}
            placement='topLeft'
            mouseEnterDelay={0.15}
            mouseLeaveDelay={0.1}
            styles={{
                body: { padding: 0, border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadius },
                root: {
                    paddingBottom: '0.25rem',
                },
            }}
            arrow={false}
        >
            <span
                style={{
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    padding: '0.125rem 0.25rem',
                    paddingLeft: '0.125rem',
                    backgroundColor: token.colorBgBase,
                }}
            >
                <strong style={{ color: token.colorText, fontWeight: 500, cursor: 'help' }}>
                    <Avatar
                        size={16}
                        shape='square'
                        style={{
                            backgroundColor: token.colorBgTextHover,
                            border: `1px solid ${token.colorBorder}`,
                            marginRight: '0.25rem',
                            marginTop: '-2px',
                        }}
                        icon={
                            <span style={{ color: token.colorTextTertiary }}>
                                {createElement(IconComponent, {
                                    size: '75%',
                                    style: { verticalAlign: '-0.15rem' },
                                })}
                            </span>
                        }
                    />
                    {capitalCase(item.label)} Add-On
                </strong>
            </span>
        </Popover>
    );
};

export default FeatureAddOnPopover;
